import React, { useMemo, useState } from "react";
import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  PrecisionManufacturing as MachineIcon,
  ChevronRight as ChevronRightIcon,
  PlayArrowRounded as PlayIcon,
  TaskAltOutlined as CompleteIcon,
  HourglassBottomRounded as WaitingIcon,
  SendOutlined as SubmitIcon,
  ScheduleOutlined,
  WarningAmberOutlined,
  CalendarTodayOutlined,
  Star as StarFilled,
  StarBorder,
  ArchiveOutlined,
  UnarchiveOutlined,
  DescriptionOutlined as FileIcon,
  LocalOfferOutlined,
} from "@mui/icons-material";
import useTranslation from "../../../shared/hooks/useTranslation";
import { Offer, OfferStatusCode, OfferFile } from "../models/models";
import { dt, StatusBadgeColorConfig } from "../../../shared/themes/redesignTokens";
import ServiceLifecycleTracker from "../../shared/components/ServiceLifecycleTracker";
import { getProviderLifecycleStages } from "../../shared/utils/lifecycleStages";
import { getProviderGuidanceCategory } from "../../shared/utils/guidanceCategories";
import ContextGuidanceBox from "../../shared/components/ContextGuidanceBox";
import TimeInStageRow from "../../shared/components/TimeInStageRow";
import ContextInfoRow from "../../shared/components/ContextInfoRow";
import dayjs from "dayjs";
import offersApi from "../api/offersApi";
import { ImagePreviewStrip, useGalleryImages } from "../../../shared/components/gallery";
import { formatUiDate } from "../../../shared/utils/dateFormat";

// ═══════════════════════════════════════════════════════════════
//  Helpers
// ═══════════════════════════════════════════════════════════════

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * formatTimeAgo — returns a human-readable "time ago" string.
 * Uses i18n translate function for localization.
 */
const formatTimeAgo = (
  isoDate: string | undefined,
  translate: (key: string, opts?: any) => any
): string | null => {
  if (!isoDate) return null;
  const now = dayjs();
  const then = dayjs(isoDate);
  const diffMin = now.diff(then, "minute");
  if (diffMin < 1) return translate("card.timeAgo.justNow");
  if (diffMin < 60) return translate("card.timeAgo.minutesAgo", { count: diffMin });
  const diffHr = now.diff(then, "hour");
  if (diffHr < 24) return translate("card.timeAgo.hoursAgo", { count: diffHr });
  const diffDay = now.diff(then, "day");
  if (diffDay < 7) return translate("card.timeAgo.daysAgo", { count: diffDay });
  return formatUiDate(then, "dayMonth");
};

/**
 * getStatusTimeIndicator — returns the relevant "time in stage" date
 * and a label key for the current offer status, used in the context strip.
 */
const getStatusTimeIndicator = (
  item: { statusCode: number; sentDate?: string; viewedDate?: string; confirmedByProviderDate?: string; confirmedArrivalDate?: string; createdDate?: string; modifiedDate?: string },
): { date?: string; labelKey: string } => {
  const S = OfferStatusCode;
  switch (item.statusCode) {
    case S.draft:
      return { date: item.createdDate, labelKey: "card.timeAgo.created" };
    case S.sent:
      return { date: item.sentDate, labelKey: "card.timeAgo.sent" };
    case S.viewed:
      return { date: item.viewedDate, labelKey: "card.timeAgo.viewed" };
    case S.awaitingConfirmation:
      return { date: item.modifiedDate, labelKey: "card.timeAgo.accepted" };
    case S.confirmed:
      return { date: item.confirmedByProviderDate, labelKey: "card.timeAgo.confirmed" };
    case S.workInProgress:
      return { date: item.confirmedArrivalDate || item.modifiedDate, labelKey: "card.timeAgo.started" };
    case S.workCompleted:
      return { date: item.modifiedDate, labelKey: "card.timeAgo.completed" };
    case S.serviceCompleted:
      return { date: item.modifiedDate, labelKey: "card.timeAgo.finished" };
    default:
      return { date: item.modifiedDate, labelKey: "card.timeAgo.updated" };
  }
};

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i;
const isOfferImageFile = (file: OfferFile): boolean => {
  return IMAGE_EXTENSIONS.test(file.name) || file.content?.startsWith("data:image/");
};

// ═══════════════════════════════════════════════════════════════
//  STATUS COLORS (shared with OfferCardCompact + OfferDetailPage)
// ═══════════════════════════════════════════════════════════════

export const statusBadgeColors: Record<number, StatusBadgeColorConfig> = {
  [OfferStatusCode.draft]: { bg: "#F5EEF8", text: "#8E44AD", border: "#D2B4DE" },
  [OfferStatusCode.sent]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [OfferStatusCode.viewed]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [OfferStatusCode.negotiating]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [OfferStatusCode.accepted]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [OfferStatusCode.rejected]: { bg: "#F9EBEA", text: "#C0392B", border: "#F5B7B1" },
  [OfferStatusCode.expired]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
  [OfferStatusCode.withdrawn]: { bg: "#F4F6F9", text: "#9CA3AF", border: "#D1D5DB" },
  [OfferStatusCode.awaitingConfirmation]: { bg: "#FEF9E7", text: "#F39C12", border: "#F9E79F" },
  [OfferStatusCode.confirmed]: { bg: "#E8F8F5", text: "#1ABC9C", border: "#A3E4D7" },
  [OfferStatusCode.workInProgress]: { bg: "#EBF5FB", text: dt.blueHover, border: "#AED6F1" },
  [OfferStatusCode.workCompleted]: { bg: "#EAFAF1", text: "#27AE60", border: "#ABEBC6" },
  [OfferStatusCode.serviceCompleted]: { bg: "#D5F5E3", text: "#1E8449", border: "#82E0AA" },
  [OfferStatusCode.archived]: { bg: "#F4F6F9", text: "#6B7280", border: "#D1D5DB" },
};

// ═══════════════════════════════════════════════════════════════
//  Left border accent color per status
// ═══════════════════════════════════════════════════════════════

const leftBorderColors: Record<number, string> = {
  [OfferStatusCode.draft]: "#D1D5DB",
  [OfferStatusCode.sent]: "#6B7280",
  [OfferStatusCode.viewed]: "#6B7280",
  [OfferStatusCode.awaitingConfirmation]: "#F59E0B",
  [OfferStatusCode.confirmed]: "#3B82F6",
  [OfferStatusCode.workInProgress]: "#2563EB",
  [OfferStatusCode.workCompleted]: "#10B981",
  [OfferStatusCode.serviceCompleted]: "#059669",
  [OfferStatusCode.accepted]: "#10B981",
  [OfferStatusCode.rejected]: "#EF4444",
  [OfferStatusCode.expired]: "#EF4444",
  [OfferStatusCode.withdrawn]: "#EF4444",
  [OfferStatusCode.negotiating]: "#F59E0B",
  [OfferStatusCode.archived]: "#D1D5DB",
};

// ═══════════════════════════════════════════════════════════════
//  Props
// ═══════════════════════════════════════════════════════════════

interface OfferCardProps {
  item: Offer;
  index: number;
  formatCurrency: (value: number) => string;
  onClick: (item: Offer) => void;
  onConfirmService?: (item: Offer) => void;
  onStartWork?: (item: Offer) => void;
  onMarkComplete?: (item: Offer) => void;
  onReview?: (item: Offer) => void;
  onSubmit?: (item: Offer) => void;
  onWithdraw?: (item: Offer) => void;
  onArchive?: (item: Offer) => void;
  onUnarchive?: (item: Offer) => void;
}

// ═══════════════════════════════════════════════════════════════
//  Expiry helpers
// ═══════════════════════════════════════════════════════════════

type ExpiryInfo = { show: boolean; isExpired: boolean; label: string; color: string; unit?: "days" | "hours"; amount?: number };

const getExpiryInfo = (item: Offer): ExpiryInfo => {
  if (!item.expiresAt) return { show: false, isExpired: false, label: "", color: "" };
  const now = dayjs();
  const expires = dayjs(item.expiresAt);
  const hoursLeft = expires.diff(now, "hour", true);
  const isSentOrViewed = item.statusCode === OfferStatusCode.sent || item.statusCode === OfferStatusCode.viewed;
  if (!isSentOrViewed) return { show: false, isExpired: false, label: "", color: "" };

  if (hoursLeft <= 0) return { show: true, isExpired: true, label: "", color: "#EF4444" };

  const daysLeft = Math.floor(hoursLeft / 24);
  if (daysLeft >= 1) {
    return { show: true, isExpired: false, label: `${daysLeft}`, unit: "days", amount: daysLeft, color: "#F59E0B" };
  }
  return { show: true, isExpired: false, label: `${Math.ceil(hoursLeft)}`, unit: "hours", amount: Math.ceil(hoursLeft), color: "#F59E0B" };
};

// ═══════════════════════════════════════════════════════════════
//  CTA Button
// ═══════════════════════════════════════════════════════════════

interface CTAConfig {
  label: string;
  color: string;
  hoverColor: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  outline?: boolean;
  pulse?: boolean;
  isIndicator?: boolean;
  compact?: boolean;
}

const CTAButton: React.FC<CTAConfig> = ({ label, color, hoverColor, onClick, icon, outline, pulse, isIndicator, compact }) => (
  <Box
    onClick={(e) => {
      e.stopPropagation();
      onClick?.();
    }}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      background: outline || isIndicator
        ? (isIndicator ? `${color}14` : "transparent")
        : `linear-gradient(135deg, ${color}, ${hoverColor})`,
      color: outline || isIndicator ? color : "#FFFFFF",
      border: outline ? `1.5px solid ${color}` : isIndicator ? `1px solid ${color}33` : "none",
      borderRadius: "24px",
      padding: "10px 16px",
      fontSize: "12px",
      fontWeight: 700,
      fontFamily: dt.font,
      cursor: isIndicator ? "default" : "pointer",
      // L6 (USER-LANG-IT-PL-1): may wrap. nowrap made DE "Aus dem Archiv wiederherstellen"
      // a 278px pill (EN 205px) that forced the 30% right zone wider than its share.
      whiteSpace: "normal",
      textAlign: "center",
      lineHeight: 1.3,
      maxWidth: "100%",
      transition: "all 0.2s ease",
      width: compact ? "auto" : "100%",
      boxSizing: "border-box",
      ...(pulse && { animation: "offerRing 2s ease-in-out infinite" }),
      ...(!isIndicator && {
        "&:hover": {
          transform: "translateY(-1px)",
          ...(outline
            ? { backgroundColor: `${color}14`, borderColor: hoverColor }
            : { boxShadow: `0 4px 12px ${color}40` }),
        },
      }),
    }}
  >
    {icon}
    {label}
    {!isIndicator && <ChevronRightIcon sx={{ fontSize: "14px" }} />}
  </Box>
);

// ═══════════════════════════════════════════════════════════════
//  Label Row style constants
// ═══════════════════════════════════════════════════════════════

const LABEL_WIDTH = "90px";
const labelSx = {
  fontSize: "11px",
  fontWeight: 600,
  textTransform: "uppercase" as const,
  letterSpacing: "0.8px",
  color: dt.textMuted,
  fontFamily: dt.font,
  // L6 (USER-LANG-IT-PL-1): wraps inside the fixed 90px lane instead of running under the
  // value (PL "FIRMA SERWISOWA" +35px, DE "ARBEITSBERICHT" +19px; EN "WORK REPORT" was +4px).
  whiteSpace: "normal" as const,
  overflowWrap: "break-word" as const,
  WebkitHyphens: "auto" as const,
  hyphens: "auto" as const,
  width: LABEL_WIDTH,
  minWidth: LABEL_WIDTH,
  flexShrink: 0,
};

// ═══════════════════════════════════════════════════════════════
//  Guidance text helper
// ═══════════════════════════════════════════════════════════════

export const getGuidanceInfo = (
  item: Offer,
  translate: (key: string, options?: any) => any
): { text: string; boldName?: string } | null => {
  const name = item.customer?.name || "";
  const S = OfferStatusCode;

  // For "Sent" status, upgrade to "Viewed" message if viewedDate exists
  if (item.statusCode === S.sent && item.viewedDate) {
    const viewDate = formatUiDate(item.viewedDate, "dayMonth");
    return { text: translate("card.guidance.viewedOn", { customerName: name, date: viewDate }), boldName: name };
  }

  switch (item.statusCode) {
    case S.draft:
      return { text: translate("card.guidance.draft") };
    case S.sent:
      return { text: translate("card.guidance.sent", { customerName: name }), boldName: name };
    case S.viewed: {
      if (item.viewedDate) {
        const viewDate = formatUiDate(item.viewedDate, "dayMonth");
        return { text: translate("card.guidance.viewedOn", { customerName: name, date: viewDate }), boldName: name };
      }
      return { text: translate("card.guidance.viewed", { customerName: name }), boldName: name };
    }
    case S.negotiating:
      return { text: translate("card.guidance.negotiating", { customerName: name }), boldName: name };
    case S.accepted:
      return { text: translate("card.guidance.accepted") };
    case S.awaitingConfirmation:
      return { text: translate("card.guidance.awaitingConfirmation", { customerName: name }), boldName: name };
    case S.confirmed: {
      const confDate = item.confirmedArrivalDate ? formatUiDate(item.confirmedArrivalDate) : "";
      const tw = item.confirmedArrivalTimeFrom && item.confirmedArrivalTimeTo
        ? ` \u00B7 ${item.confirmedArrivalTimeFrom.slice(0, 5)}\u2013${item.confirmedArrivalTimeTo.slice(0, 5)}`
        : "";
      return { text: translate("card.guidance.confirmed", { date: confDate, timeWindow: tw }) };
    }
    case S.workInProgress:
      return { text: translate("card.guidance.workInProgress", { customerName: name }), boldName: name };
    case S.workCompleted:
      return { text: translate("card.guidance.workCompleted", { customerName: name }), boldName: name };
    case S.serviceCompleted:
      if (item.reviewRating != null) {
        return { text: translate("card.guidance.serviceCompletedWithReview", { rating: item.reviewRating }) };
      }
      return { text: translate("card.guidance.serviceCompleted") };
    case S.rejected:
      return { text: translate("card.guidance.rejected", { customerName: name }), boldName: name };
    case S.expired: {
      const expDate = item.expiresAt ? formatUiDate(item.expiresAt) : "";
      return { text: translate("card.guidance.expired", { date: expDate }) };
    }
    case S.withdrawn:
      return { text: translate("card.guidance.withdrawn") };
    case S.archived:
      return { text: translate("card.guidance.archived") };
    default:
      return null;
  }
};

// ═══════════════════════════════════════════════════════════════
//  Guidance text renderer — makes customer name bold
// ═══════════════════════════════════════════════════════════════

export const GuidanceText: React.FC<{ text: string; boldName?: string }> = ({ text, boldName }) => {
  if (!boldName || !text.includes(boldName)) {
    return <>{text}</>;
  }
  const idx = text.indexOf(boldName);
  const before = text.slice(0, idx);
  const after = text.slice(idx + boldName.length);
  return (
    <>
      {before}
      <Box component="span" sx={{ fontWeight: 600 }}>{boldName}</Box>
      {after}
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
//  OfferCard
// ═══════════════════════════════════════════════════════════════

const OfferCard: React.FC<OfferCardProps> = ({
  item,
  index,
  formatCurrency,
  onClick,
  onConfirmService,
  onStartWork,
  onMarkComplete,
  onReview,
  onSubmit,
  onWithdraw,
  onArchive,
  onUnarchive,
}) => {
  const { translate } = useTranslation(["serviceOffers", "common"]);
  const navigate = useNavigate();
  const isPhone = useMediaQuery("(max-width:768px)");
  const borderColor = leftBorderColors[item.statusCode] || "#6B7280";

  const isArchived = item.statusCode === OfferStatusCode.archived;
  const serviceDate = item.confirmedArrivalDate || item.earliestStartDate;
  const expiry = getExpiryInfo(item);
  // "3 days" / "1 hour" — plural keys, rendered after "Expires in"
  const expiryText = expiry.unit === "hours"
    ? translate("card.hoursCount", { count: expiry.amount ?? 0 })
    : translate("card.daysCount", { count: expiry.amount ?? 0 });

  // Time window string
  const timeFrom = item.confirmedArrivalTimeFrom?.slice(0, 5);
  const timeTo = item.confirmedArrivalTimeTo?.slice(0, 5);
  const timeWindow = timeFrom && timeTo ? ` \u00B7 ${timeFrom}\u2013${timeTo}` : "";

  // Machine info
  const machine = item.serviceRequest?.machine;
  const machineName = machine
    ? (machine.brandName ? `${machine.brandName} ${machine.name}` : machine.name)
    : "";
  const machineImage = machine?.imageUrl || machine?.catalogImageUrl;

  // ── CTA config ──
  const getCTA = (): CTAConfig | null => {
    switch (item.statusCode) {
      case OfferStatusCode.awaitingConfirmation:
        return { label: translate("confirmService"), color: "#F59E0B", hoverColor: "#D97706", onClick: () => onConfirmService?.(item), pulse: true };
      case OfferStatusCode.confirmed:
        return { label: translate("inlineActions.startWork"), color: "#14B8A6", hoverColor: "#0D9488", onClick: () => onStartWork?.(item), icon: <PlayIcon sx={{ fontSize: "14px" }} /> };
      case OfferStatusCode.workInProgress:
        return { label: translate("inlineActions.complete"), color: "#3B82F6", hoverColor: "#2563EB", onClick: () => onMarkComplete?.(item), icon: <CompleteIcon sx={{ fontSize: "14px" }} /> };
      case OfferStatusCode.workCompleted:
        return { label: translate("inlineActions.waiting"), color: "#16A34A", hoverColor: "#16A34A", isIndicator: true, icon: <WaitingIcon sx={{ fontSize: "14px", animation: "spin 2s linear infinite" }} /> };
      case OfferStatusCode.serviceCompleted:
        return { label: translate("card.archive"), color: "#6B7280", hoverColor: "#4B5563", outline: true, onClick: () => onArchive?.(item), icon: <ArchiveOutlined sx={{ fontSize: "14px" }} /> };
      case OfferStatusCode.archived:
        return { label: translate("card.unarchive"), color: "#27AE60", hoverColor: "#2ECC71", onClick: () => onUnarchive?.(item), icon: <UnarchiveOutlined sx={{ fontSize: "14px" }} /> };
      case OfferStatusCode.draft:
        return { label: translate("submitOffer"), color: "#8E44AD", hoverColor: "#7D3C98", onClick: () => onSubmit?.(item), icon: <SubmitIcon sx={{ fontSize: "14px" }} /> };
      case OfferStatusCode.sent:
        return { label: translate("card.awaitingResponse"), color: "#6B7280", hoverColor: "#6B7280", isIndicator: true, icon: <WaitingIcon sx={{ fontSize: "14px", animation: "spin 2s linear infinite" }} /> };
      case OfferStatusCode.viewed:
        return { label: translate("card.underReview"), color: "#6B7280", hoverColor: "#6B7280", isIndicator: true, icon: <WaitingIcon sx={{ fontSize: "14px", animation: "spin 2s linear infinite" }} /> };
      default:
        return null;
    }
  };

  const cta = getCTA();
  const guidance = getGuidanceInfo(item, translate);

  // Image gallery — separate image files from non-image files
  const imageFiles = useMemo(() => (item.files || []).filter(isOfferImageFile), [item.files]);
  const galleryImages = useGalleryImages({ type: "entityFiles", files: imageFiles });

  // File download/view state
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null);

  // Resolve file content as data URI (fetches full offer if content missing)
  const resolveFileContent = async (fileName: string): Promise<string> => {
    const fullOffer = await offersApi.getOffer(item.id);
    const file = fullOffer.files?.find(f => f.name === fileName);
    if (!file?.content) return "";

    const content = file.content;
    // Content can be: a URL (https://...), a data URI (data:...), or raw base64
    if (content.startsWith("http://") || content.startsWith("https://") || content.startsWith("data:")) {
      return content;
    }
    // Raw base64 — prepend data URI prefix
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    const mimeTypes: Record<string, string> = {
      pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg",
      gif: "image/gif", webp: "image/webp", doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel", xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
    const mime = mimeTypes[ext] || "application/octet-stream";
    return `data:${mime};base64,${content}`;
  };

  // View file: opens in new tab
  const handleFileView = async (e: React.MouseEvent, fileName: string) => {
    e.stopPropagation();
    if (downloadingFile) return;
    setDownloadingFile(fileName);
    try {
      const content = await resolveFileContent(fileName);
      if (!content) return;
      const link = document.createElement("a");
      link.href = content;
      link.target = "_blank";
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to view file:", err);
    } finally {
      setDownloadingFile(null);
    }
  };

  // Time-in-stage info — computed for context panel
  const timeIndicatorC = getStatusTimeIndicator(item);
  const timeAgoStrC = formatTimeAgo(timeIndicatorC.date, translate);

  // ── Shared JSX fragments ────────────────────────────────

  /** Header strip (gray bg) — used by all variants */
  const headerJsx = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: isPhone ? "12px 16px" : "12px 24px",
        backgroundColor: "#F8FAFC",
        borderBottom: `1px solid ${dt.cardBorder}`,
      }}
    >
      <Box
        sx={{
          fontWeight: 700,
          fontSize: "16px",
          fontFamily: dt.font,
          color: "#0F172A",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        }}
      >
        {item.serviceRequest?.issue || "\u2014"}
      </Box>
      <Box sx={{ flexShrink: 0, textAlign: "right", ml: "12px" }}>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onClick(item);
          }}
          sx={{ fontSize: "12px", fontFamily: dt.fontMono, color: dt.textLink, fontWeight: 600, cursor: "pointer", lineHeight: 1.4, "&:hover": { textDecoration: "underline" } }}
        >
          {item.internalNumber}
        </Box>
        {item.serviceRequest?.internalNumber && (
          <>
            <Box sx={{ fontSize: "10px", color: dt.textMuted, lineHeight: 1.4 }}>
              {translate("card.for")}
            </Box>
            <Box
              component="span"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                if (item.serviceRequest?.id) navigate(`/service-board/${item.serviceRequest.id}`);
              }}
              sx={{
                fontSize: "12px",
                fontFamily: dt.fontMono,
                color: dt.textLink,
                lineHeight: 1.4,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {item.serviceRequest.internalNumber}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );

  /** Content section (customer, machine, review, attachments) */
  const contentJsx = (
    <Box sx={{ padding: isPhone ? "14px 16px" : "16px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>

      {/* CUSTOMER */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <Box component="span" sx={labelSx}>
          {translate("card.customer")}
        </Box>
        {item.customer?.logoUrl ? (
          <Box
            component="img"
            src={item.customer.logoUrl}
            alt=""
            sx={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              objectFit: "cover",
              flexShrink: 0,
              border: `1px solid ${dt.cardBorder}`,
            }}
          />
        ) : (
          <Box
            sx={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: dt.blueSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ fontSize: "13px", fontWeight: 700, color: dt.blue, fontFamily: dt.font }}>
              {item.customer?.name?.charAt(0)?.toUpperCase() || "?"}
            </Box>
          </Box>
        )}
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (item.customer?.id) navigate(`/companies/${item.customer.id}`);
          }}
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: dt.textLink,
            fontFamily: dt.font,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {item.customer?.name}
        </Box>
      </Box>

      {/* MACHINE */}
      {machine && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box component="span" sx={labelSx}>
            {translate("card.machine")}
          </Box>
          {machineImage ? (
            <Box
              component="img"
              src={machineImage}
              alt=""
              sx={{
                width: "40px",
                height: "40px",
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
          ) : (
            <Box
              sx={{
                width: "40px",
                height: "40px",
                borderRadius: dt.radiusSm,
                border: `1px solid ${dt.cardBorder}`,
                backgroundColor: dt.inputBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MachineIcon sx={{ fontSize: "20px", color: dt.textMuted }} />
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#1E293B",
                fontFamily: dt.font,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {machineName}
            </Box>
          </Box>
        </Box>
      )}

      {/* CUSTOMER REVIEW (only when review exists) */}
      {item.reviewRating != null && (
        <Box>
          <Box
            sx={{
              backgroundColor: "#FAFDF6",
              border: "1px solid #D1FAE5",
              borderLeft: "3px solid #059669",
              borderRadius: "8px",
              padding: "12px 14px",
            }}
          >
            {/* Label + stars + date row */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px", flexWrap: "wrap", gap: "4px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box sx={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("card.customerReview")}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "1px" }}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    star <= (item.reviewRating || 0)
                      ? <StarFilled key={star} sx={{ fontSize: "18px", color: "#F59E0B" }} />
                      : <StarBorder key={star} sx={{ fontSize: "18px", color: "#D1D5DB" }} />
                  ))}
                </Box>
              </Box>
              {item.reviewDate && (
                <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}>
                  {formatUiDate(item.reviewDate)}
                </Box>
              )}
            </Box>
            {/* Review title */}
            {item.reviewTitle && (
              <Box sx={{ fontSize: "14px", fontWeight: 700, color: "#0F172A", fontFamily: dt.font, mb: "4px" }}>
                {item.reviewTitle}
              </Box>
            )}
            {/* Review comment */}
            {item.reviewComment && (
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: "#475569",
                  fontFamily: dt.font,
                  lineHeight: 1.4,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {item.reviewComment}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* OFFER ATTACHMENTS (when offer has files) */}
      {item.files && item.files.length > 0 && (
        <Box>
          <Box sx={{ fontSize: "10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "6px" }}>
            {translate("card.attachments")} ({item.files.length})
          </Box>
          {galleryImages.length > 0 && (
            <Box sx={{ mb: "6px" }}>
              <ImagePreviewStrip images={galleryImages} maxVisible={3} />
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {item.files.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid transparent",
                  width: "fit-content",
                  maxWidth: "100%",
                  transition: "all 0.15s ease",
                }}
              >
                {downloadingFile === file.name ? (
                  <CircularProgress size={14} sx={{ color: dt.blue, flexShrink: 0 }} />
                ) : (
                  <FileIcon sx={{ fontSize: 16, color: dt.textLink, flexShrink: 0 }} />
                )}
                <Box
                  onClick={(e) => handleFileView(e, file.name)}
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: dt.textLink,
                    fontFamily: dt.font,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    cursor: downloadingFile === file.name ? "wait" : "pointer",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {file.name}
                </Box>
                <Box sx={{ fontSize: "10px", color: dt.textMuted, fontFamily: dt.fontMono, flexShrink: 0 }}>
                  {formatFileSize(file.size)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  /** Bottom info strip (submitted, service date, expiry, price) */
  const bottomStripJsx = (
    <Box
      sx={{
        borderTop: `1px solid ${dt.cardBorder}`,
        backgroundColor: "#F8FAFC",
        padding: isPhone ? "12px 16px" : "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "16px",
        flexWrap: "wrap",
        mt: "auto",
      }}
    >
      {/* SUBMITTED */}
      {item.createdDate && (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
            {translate("card.submitted")}
          </Box>
          <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B", fontFamily: dt.font }}>
            {formatUiDate(item.createdDate)}
          </Box>
        </Box>
      )}

      {/* SERVICE DATE */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
          {translate("card.serviceDate")}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <ScheduleOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />
          <Box sx={{ fontSize: "13px", fontWeight: 500, color: "#1E293B", fontFamily: dt.font }}>
            {serviceDate ? formatUiDate(serviceDate) : "\u2014"}
            {timeWindow && (
              <Box component="span" sx={{ color: dt.textMuted, fontFamily: dt.fontMono, fontSize: "12px" }}>
                {timeWindow}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* EXPIRES IN (only for sent/viewed with expiresAt) */}
      {expiry.show && (
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
            {translate("card.expiresIn")}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <WarningAmberOutlined sx={{ fontSize: "14px", color: expiry.color }} />
            <Box sx={{ fontSize: "13px", fontWeight: 500, color: expiry.color, fontFamily: dt.font }}>
              {expiry.isExpired
                ? translate("card.expired")
                : expiryText}
            </Box>
          </Box>
        </Box>
      )}

      {/* OFFER PRICE */}
      <Box sx={{ minWidth: 0, textAlign: "right", ml: "auto" }}>
        <Box sx={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.8px", color: dt.textMuted, fontFamily: dt.font, mb: "2px" }}>
          {translate("card.offerPrice")}
        </Box>
        <Box sx={{ fontSize: "20px", fontWeight: 700, fontFamily: dt.fontMono, color: "#1E293B" }}>
          {formatCurrency(item.totalEstimatedCost)}
        </Box>
      </Box>
    </Box>
  );

  /** Right zone — stepper, context panel, CTA */
  const rightZoneJsx = (
    <Box
      sx={{
        flex: isPhone ? "none" : "3",
        maxWidth: isPhone ? "100%" : "none",
        display: "flex",
        flexDirection: "column",
        padding: "20px 20px",
        borderLeft: isPhone ? "none" : `1px solid ${dt.cardBorder}`,
        borderTop: isPhone ? `1px solid ${dt.cardBorder}` : "none",
        gap: "12px",
      }}
    >
      {/* PROGRESS — stepper */}
      {item.statusCode !== OfferStatusCode.draft && (
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "1px",
              color: dt.textMuted,
              fontFamily: dt.font,
              mb: "6px",
            }}
          >
            {translate("card.progress")}
          </Box>
          {!isPhone ? (
            <>
              {/* Horizontal stepper */}
              <ServiceLifecycleTracker
                stages={getProviderLifecycleStages(item.statusCode)}
                variant="card-horizontal"
              />

              {/* Status Context Area — color-coded per-status guidance + data rows */}
              <Box sx={{ mt: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                {/* Color-coded guidance box */}
                {guidance && (
                  <ContextGuidanceBox
                    text={guidance.text}
                    boldName={guidance.boldName}
                    category={getProviderGuidanceCategory(item.statusCode)}
                  />
                )}

                {/* Time-in-stage indicator */}
                {timeAgoStrC && (
                  <TimeInStageRow
                    label={translate(timeIndicatorC.labelKey)}
                    timeAgo={timeAgoStrC}
                  />
                )}

                {/* ── Per-stage contextual data rows (max 3) ── */}

                {/* Expiry countdown (sent/viewed) */}
                {expiry.show && (
                  <ContextInfoRow
                    icon={<WarningAmberOutlined sx={{ fontSize: "14px", color: expiry.color }} />}
                    color={expiry.color}
                  >
                    {expiry.isExpired
                      ? translate("card.contextRows.expiredOn")
                      : translate("card.contextRows.expiryCountdown", {
                          value: expiryText,
                        })}
                  </ContextInfoRow>
                )}

                {/* Total price (always useful context) */}
                {item.totalEstimatedCost > 0 && [OfferStatusCode.sent, OfferStatusCode.viewed, OfferStatusCode.negotiating, OfferStatusCode.workCompleted, OfferStatusCode.rejected].includes(item.statusCode) && (
                  <ContextInfoRow
                    icon={<LocalOfferOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />}
                  >
                    <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 600 }}>
                      {translate("card.contextRows.totalPrice", { price: formatCurrency(item.totalEstimatedCost) })}
                    </Box>
                  </ContextInfoRow>
                )}

                {/* Confirmation deadline (awaiting confirmation) */}
                {item.confirmationDeadline && item.statusCode === OfferStatusCode.awaitingConfirmation && (
                  <ContextInfoRow
                    icon={<WarningAmberOutlined sx={{ fontSize: "14px", color: "#F59E0B" }} />}
                    color="#F59E0B"
                  >
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      {translate("card.contextRows.confirmDeadline", { deadline: formatUiDate(item.confirmationDeadline, "dayMonthTime") })}
                    </Box>
                  </ContextInfoRow>
                )}

                {/* Service date + time window (confirmed/working) */}
                {item.confirmedArrivalDate && [OfferStatusCode.confirmed, OfferStatusCode.workInProgress].includes(item.statusCode) && (() => {
                  const now = dayjs();
                  const arrDate = dayjs(item.confirmedArrivalDate);
                  const daysDiff = arrDate.startOf("day").diff(now.startOf("day"), "day");
                  const daysUntilLabel = daysDiff === 0
                    ? translate("card.contextRows.today")
                    : daysDiff === 1
                    ? translate("card.contextRows.tomorrow")
                    : translate("card.contextRows.daysLeft", { count: daysDiff });
                  const twStr = item.confirmedArrivalTimeFrom && item.confirmedArrivalTimeTo
                    ? `${item.confirmedArrivalTimeFrom.slice(0, 5)}\u2013${item.confirmedArrivalTimeTo.slice(0, 5)}`
                    : "";
                  return (
                    <ContextInfoRow
                      icon={<CalendarTodayOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />}
                    >
                      <Box component="span" sx={{ fontFamily: dt.fontMono, fontWeight: 600, color: daysDiff <= 1 ? "#F59E0B" : "#1E293B" }}>
                        {daysUntilLabel}
                        {twStr && <Box component="span" sx={{ color: dt.textMuted, fontWeight: 500 }}>{` \u00B7 ${twStr}`}</Box>}
                      </Box>
                    </ContextInfoRow>
                  );
                })()}

                {/* Travel info (confirmed/working, when available) */}
                {item.travelDistanceKm && [OfferStatusCode.confirmed, OfferStatusCode.workInProgress].includes(item.statusCode) && (
                  <ContextInfoRow
                    icon={<Box component="span" sx={{ fontSize: "12px", color: dt.textMuted }}>{"\uD83D\uDCCD"}</Box>}
                  >
                    <Box component="span" sx={{ fontFamily: dt.fontMono }}>
                      {translate("card.contextRows.travelInfo", {
                        distance: Math.round(item.travelDistanceKm),
                        duration: item.travelTimeHours ? Math.round(item.travelTimeHours * 60) : "?",
                      })}
                    </Box>
                  </ContextInfoRow>
                )}

                {/* Estimated duration (work in progress) */}
                {item.estimatedDuration && item.statusCode === OfferStatusCode.workInProgress && (
                  <ContextInfoRow
                    icon={<ScheduleOutlined sx={{ fontSize: "14px", color: dt.textMuted }} />}
                  >
                    {translate("card.contextRows.estimatedDuration", { duration: item.estimatedDuration })}
                  </ContextInfoRow>
                )}

                {/* Files count (work completed — shows how much evidence is attached) */}
                {item.files && item.files.length > 0 && item.statusCode === OfferStatusCode.workCompleted && (
                  <ContextInfoRow
                    icon={<FileIcon sx={{ fontSize: "14px", color: dt.textMuted }} />}
                  >
                    {translate("card.contextRows.filesAttached", { count: item.files.length })}
                  </ContextInfoRow>
                )}
              </Box>
            </>
          ) : (
            <ServiceLifecycleTracker
              stages={getProviderLifecycleStages(item.statusCode)}
              variant="compact"
            />
          )}
        </Box>
      )}

      {/* CTA button pinned to bottom */}
      {cta && (
        <Box sx={{ mt: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
          <CTAButton {...cta} compact />
          <Box
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/service-offers/${item.id}`);
            }}
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.textLink,
              cursor: "pointer",
              textDecoration: "none",
              transition: "opacity 0.15s",
              "&:hover": { opacity: 0.7 },
            }}
          >
            {translate("offerDetail.viewFullDetails")}
          </Box>
        </Box>
      )}
      {!cta && (
        <Box sx={{ mt: "auto", display: "flex", justifyContent: "center" }}>
          <Box
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/service-offers/${item.id}`);
            }}
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.textLink,
              cursor: "pointer",
              textDecoration: "none",
              transition: "opacity 0.15s",
              "&:hover": { opacity: 0.7 },
            }}
          >
            {translate("offerDetail.viewFullDetails")}
          </Box>
        </Box>
      )}
    </Box>
  );

  // ── Card outer shell ────────────────────────────────────

  const cardOuterSx = {
    backgroundColor: dt.cardBg,
    borderRadius: dt.radiusSm,
    border: `1px solid ${dt.cardBorder}`,
    borderLeft: `4px solid ${borderColor}`,
    boxShadow: dt.cardShadow,
    overflow: "hidden",
    width: "100%",
    boxSizing: "border-box" as const,
    animation: `cardReveal 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.06}s both`,
    "@keyframes cardReveal": {
      "0%": { opacity: 0, transform: "translateY(16px) scale(0.98)" },
      "100%": { opacity: 1, transform: "translateY(0) scale(1)" },
    },
    transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      boxShadow: dt.cardShadowHover,
    },
    ...(expiry.isExpired && { opacity: 0.65 }),
    ...(isArchived && { opacity: 0.7 }),
  };

  // ── Card render — 2-zone row layout ───────────
  return (
    <Box
      sx={{
        ...cardOuterSx,
        display: "flex",
        flexDirection: isPhone ? "column" : "row",
      }}
    >
      {/* LEFT ZONE (includes bottom strip) */}
      <Box sx={{ flex: "7", display: "flex", flexDirection: "column", minWidth: 0 }}>
        {headerJsx}
        {contentJsx}
        {bottomStripJsx}
      </Box>
      {/* RIGHT ZONE */}
      {rightZoneJsx}
    </Box>
  );
};

export default OfferCard;
